import { formatCentsString } from '@/shared/lib/money';

export interface OpenAccountAbonoPrintPayment {
  payment_method_name: string;
  amount_cents: string;
  reference_number: string | null;
}

export interface OpenAccountAbonoPrintConcept {
  item_code: string;
  item_name: string;
  quantity: string;
  covered_cents: string;
}

export interface OpenAccountAbonoTicketPrintArgs {
  account_folio_number: string | null;
  account_id_fallback: string;
  tutor_name: string | null;
  pet_name: string | null;
  total_cents: string;
  balance_before_cents: string;
  balance_after_cents: string;
  ticket_folio_number: string | null;
  covered_concepts: readonly OpenAccountAbonoPrintConcept[];
  payments: readonly OpenAccountAbonoPrintPayment[];
  cashier_name: string;
  note: string | null;
  paid_at_iso: string | null;
  mode: 'ticket' | 'reprint';
}

const TICKET_LOGO_PATH = '/totem_logo_ticket.png';
const TICKET_DEFAULT_COPIES = 2;
let cached_ticket_logo_data_uri: string | null | undefined;

export async function printOpenAccountAbonoTicket(
  args: OpenAccountAbonoTicketPrintArgs,
): Promise<void> {
  if (typeof window === 'undefined') return;
  const logo_data_uri = await resolveTicketLogoDataUri();
  const printable = buildOpenAccountAbonoTicketHtml(args, logo_data_uri);
  const popup = window.open('', '_blank', 'width=420,height=720');
  if (!popup) {
    const current = document.body.innerHTML;
    document.body.innerHTML = printable;
    window.print();
    document.body.innerHTML = current;
    window.location.reload();
    return;
  }
  popup.document.open();
  popup.document.write(printable);
  popup.document.close();
  await waitForPopupReady(popup);
  await waitForPopupImages(popup);
  popup.addEventListener(
    'afterprint',
    () => {
      popup.close();
    },
    { once: true },
  );
  popup.focus();
  popup.print();
}

function buildOpenAccountAbonoTicketHtml(
  args: OpenAccountAbonoTicketPrintArgs,
  logo_data_uri: string | null,
): string {
  const paid_at = args.paid_at_iso ?? new Date().toISOString();
  const now_label = escapeHtml(dateTimeLabel(paid_at));
  const account_label = args.account_folio_number ?? args.account_id_fallback.slice(0, 8);
  const ticket_number = escapeHtml(
    args.ticket_folio_number ? `T-${args.ticket_folio_number}` : `A-${account_label}`,
  );
  const cashier = escapeHtml(args.cashier_name);
  const tutor = escapeHtml(args.tutor_name ?? 'Usuario General');
  const pet = escapeHtml(args.pet_name ?? 'Sin mascota');
  const logo_markup = logo_data_uri
    ? `<img class="logo" src="${escapeHtml(logo_data_uri)}" alt="Totem" />`
    : '<p class="logo-fallback">TOTEM</p>';
  const payment_rows = args.payments
    .map((payment) => {
      const ref = payment.reference_number?.trim();
      return `
        <tr>
          <td>${escapeHtml(payment.payment_method_name)}${ref ? `<br /><span class="muted">Ref. ${escapeHtml(ref)}</span>` : ''}</td>
          <td class="num">${escapeHtml(formatCentsString(payment.amount_cents))}</td>
        </tr>`;
    })
    .join('');
  const concept_rows =
    args.covered_concepts.length > 0
      ? args.covered_concepts
          .map(
            (concept) => `
        <tr>
          <td>${escapeHtml(concept.item_name)}<br /><span class="muted">${escapeHtml(concept.item_code)} &middot; ${escapeHtml(concept.quantity)}</span></td>
          <td class="num">${escapeHtml(formatCentsString(concept.covered_cents))}</td>
        </tr>`,
          )
          .join('')
      : '<tr><td colspan="2" class="muted">Aportacion aplicada a saldo de cuenta abierta.</td></tr>';
  const note_markup = args.note
    ? `<p class="line"><strong>Nota:</strong> ${escapeHtml(args.note)}</p>`
    : '';
  const ticket_kind = args.mode === 'reprint' ? 'REIMPRESION' : 'APORTACION A CUENTA ABIERTA';
  const copy = `
    <section class="ticket">
      <p class="kind">${escapeHtml(ticket_kind)}</p>
      <div class="logo-wrap">
        ${logo_markup}
      </div>
      <p class="brand">Hospital Veterinario de Especialidades Totem</p>
      <p class="line">Direccion: Prolongacion Cuauhtemoc 224. Huizache 1, Durango, Mexico</p>
      <p class="line">Telefono: 618 112 3896</p>
      <p class="line">Fecha: ${now_label}</p>
      <p class="line">No de Ticket: ${ticket_number}</p>
      <p class="line">Le Atendio: ${cashier}</p>
      <hr />
      <p class="line">Nombre cliente: ${tutor}</p>
      <p class="line">Mascota: ${pet}</p>
      <p class="line">Cuenta: #${escapeHtml(account_label)}</p>
      <hr />
      <table>
        <thead><tr><th>Concepto</th><th class="num">Total</th></tr></thead>
        <tbody>
          <tr>
            <td>Aportacion a cuenta</td>
            <td class="num">${escapeHtml(formatCentsString(args.total_cents))}</td>
          </tr>
        </tbody>
      </table>
      <hr />
      <p class="total">Aportacion ${escapeHtml(formatCentsString(args.total_cents))}</p>
      <p class="line"><strong>Saldo anterior:</strong> ${escapeHtml(formatCentsString(args.balance_before_cents))}</p>
      <p class="line"><strong>Saldo restante:</strong> ${escapeHtml(formatCentsString(args.balance_after_cents))}</p>
      <hr />
      <p class="line"><strong>Pagos recibidos</strong></p>
      <table><tbody>${payment_rows}</tbody></table>
      <hr />
      <p class="line"><strong>Aplicado a</strong></p>
      <table><tbody>${concept_rows}</tbody></table>
      ${note_markup}
      <div class="footer">
        <p class="footer-main">CONSERVE ESTE COMPROBANTE</p>
        <p class="footer-sub">ES INDISPENSABLE PARA CUALQUIER ACLARACION</p>
        <p class="footer-thanks">GRACIAS POR TU VISITA!</p>
        <p class="footer-sub">NO OLVIDES SEGUIRNOS EN NUESTRAS REDES SOCIALES</p>
        <p class="footer-social">@hospital.totem</p>
        <p class="footer-main">DUDAS O ACLARACIONES:</p>
        <p class="footer-contact">618 112 3896</p>
      </div>
    </section>`;
  return `
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Ticket ${ticket_number}</title>
        <style>
          @page { size: 80mm auto; margin: 0; }
          html, body { margin: 0; width: 80mm; }
          body { font-family: Arial, Helvetica, sans-serif; color: #111; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .ticket { width: 80mm; box-sizing: border-box; padding: 2.2mm 2.2mm 2mm; page-break-after: always; break-after: page; }
          .ticket:last-child { page-break-after: auto; break-after: auto; }
          p { margin: 2px 0; font-size: 11px; line-height: 1.2; }
          .kind { margin: 0 0 4px; text-align: center; font-size: 12px; font-weight: 800; letter-spacing: 0.4px; }
          .logo-wrap { text-align: center; margin: 0 0 4px; }
          .logo { width: 52mm; max-width: 100%; height: auto; }
          .logo-fallback { margin: 0; font-size: 16px; font-weight: 800; letter-spacing: 0.8px; }
          .brand { text-align: center; font-weight: 700; font-size: 11px; margin-bottom: 3px; }
          .line { margin: 1px 0; }
          table { width: 100%; border-collapse: collapse; margin-top: 6px; font-size: 10px; }
          th { text-align: left; border-bottom: 1px solid #111; padding-bottom: 2px; }
          td { padding: 2px 0; vertical-align: top; }
          .num { text-align: right; white-space: nowrap; }
          .muted { color: #555; font-size: 9px; }
          .total { font-size: 13px; font-weight: 800; text-align: right; margin-top: 4px; }
          .footer { text-align: center; margin-top: 8px; border-top: 1px solid #111; padding-top: 6px; }
          .footer-main { font-weight: 800; font-size: 11px; margin: 2px 0; }
          .footer-sub { font-size: 9px; margin: 2px 0; }
          .footer-thanks { font-weight: 900; font-size: 18px; line-height: 1; margin: 4px 0; }
          .footer-social { font-weight: 700; font-size: 14px; line-height: 1; margin: 3px 0; }
          .footer-contact { font-weight: 700; font-size: 15px; line-height: 1; margin: 3px 0; }
          hr { border: 0; border-top: 1px solid #111; margin: 5px 0; }
          @media print {
            html, body { width: 80mm; }
            .ticket { page-break-after: always; break-after: page; }
            .ticket:last-child { page-break-after: auto; break-after: auto; }
          }
        </style>
      </head>
      <body>${Array.from({ length: TICKET_DEFAULT_COPIES }, () => copy).join('')}</body>
    </html>`;
}

async function resolveTicketLogoDataUri(): Promise<string | null> {
  if (cached_ticket_logo_data_uri !== undefined) return cached_ticket_logo_data_uri;
  try {
    const response = await fetch(TICKET_LOGO_PATH, { cache: 'force-cache' });
    if (!response.ok) {
      cached_ticket_logo_data_uri = null;
      return null;
    }
    const blob = await response.blob();
    cached_ticket_logo_data_uri = await blobToDataUri(blob);
    return cached_ticket_logo_data_uri;
  } catch {
    cached_ticket_logo_data_uri = null;
    return null;
  }
}

function blobToDataUri(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('logo_read_failed'));
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }
      reject(new Error('logo_read_failed'));
    };
    reader.readAsDataURL(blob);
  });
}

async function waitForPopupReady(popup: Window): Promise<void> {
  if (popup.document.readyState === 'complete') return;
  await Promise.race([
    new Promise<void>((resolve) => popup.addEventListener('load', () => resolve(), { once: true })),
    delay(1200),
  ]);
}

async function waitForPopupImages(popup: Window): Promise<void> {
  const images = Array.from(popup.document.images ?? []);
  if (images.length === 0) return;
  await Promise.race([
    Promise.all(
      images.map((image) =>
        image.complete
          ? Promise.resolve()
          : new Promise<void>((resolve) => {
              image.addEventListener('load', () => resolve(), { once: true });
              image.addEventListener('error', () => resolve(), { once: true });
            }),
      ),
    ).then(() => undefined),
    delay(1400),
  ]);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function dateTimeLabel(iso: string): string {
  try {
    return new Date(iso).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
